"use strict";
/**
 * `Interpolate` is a modified implementation of `react-i18next`'s
 * `Trans` component. The original implementation depends on React.Context
 * (which we currently can't guarantee support for from existing React projects)
 * to manage the i18n instance.
 *
 * https://github.com/i18next/react-i18next/blob/master/src/Trans.js
 *
 * The original implementation also requires `i18next` (and therefore lib-i18n)
 * to be initialized with `react-i18next` as a dependendency, even though we
 * are only interested in a single piece of functionality- the interpolation of
 * strings with components mixed together.
 *
 * Here we modify the original source so that we are now passing the instance of
 * i18n as a prop, as well as setting defaults to some variables that would
 * have otherwise been deduced via the configuration settings initialized by
 * `react-i18next` and stored in React Context.
 *
 *
 * TODO:
 * 1.) Add test coverage
 * 2.) Consider cleaning up code that isn't needed for our use cases; Simplify
 *     component API and delete unnecessary code
 * 3.) Investigate more edge-cases and alternate approaches for handling
 *     component interpolation.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.nodesToString = void 0;
/* eslint-disable no-param-reassign */
var HTML = require("html-parse-stringify2");
var React = require("react");
// tslint:disable
var warn = console.error;
var warnOnce = console.error;
function hasChildren(node) {
    return node && (node.children || (node.props && node.props.children));
}
function getChildren(node) {
    // tslint:disable-next-line: curly
    if (!node)
        return [];
    return node && node.children
        ? node.children
        : node.props && node.props.children;
}
function hasValidReactChildren(children) {
    // tslint:disable-next-line: curly
    if (Object.prototype.toString.call(children) !== '[object Array]')
        return false;
    return children.every(function (child) { return React.isValidElement(child); });
}
// Disabling due to legacy reasons, to decrease noise
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function nodesToString(mem, children, index, i18nOptions) {
    if (!children)
        return '';
    // tslint:disable-next-line: curly
    if (Object.prototype.toString.call(children) !== '[object Array]')
        children = [children];
    var keepArray = i18nOptions.transKeepBasicHtmlNodesFor || []; // ['br', 'strong', 'i']
    children.forEach(function (child, i) {
        // const isElement = React.isValidElement(child);
        // tslint:disable-next-line: max-line-length
        // const elementKey = `${index !== 0 ? index + '-' : ''}${i}:${typeof child.type === 'function' ? child.type.name : child.type || 'var'}`;
        var elementKey = "" + i;
        if (typeof child === 'string') {
            mem = "" + mem + child;
        }
        else if (hasChildren(child)) {
            var elementTag = keepArray.indexOf(child.type) > -1 &&
                Object.keys(child.props).length === 1 &&
                typeof hasChildren(child) === 'string'
                ? child.type
                : elementKey;
            if (child.props && child.props.i18nIsDynamicList) {
                // we got a dynamic list like "<ul>{['a', 'b'].map(item => ( <li key={item}>{item}</li> ))}</ul>""
                // the result should be "<0></0>" and not "<0><0>a</0><1>b</1></0>"
                mem = mem + "<" + elementTag + "></" + elementTag + ">";
            }
            else {
                // regular case mapping the inner children
                mem = mem + "<" + elementTag + ">" + nodesToString('', getChildren(child), i + 1, i18nOptions) + "</" + elementTag + ">";
            }
        }
        else if (React.isValidElement(child)) {
            if (keepArray.indexOf(child.type) > -1 &&
                Object.keys(child.props).length === 0) {
                mem = mem + "<" + child.type + "/>";
            }
            else {
                mem = mem + "<" + elementKey + "></" + elementKey + ">";
            }
        }
        else if (typeof child === 'object') {
            var clone = __assign({}, child);
            var format = clone.format;
            delete clone.format;
            var keys = Object.keys(clone);
            if (format && keys.length === 1) {
                mem = mem + "{{" + keys[0] + ", " + format + "}}";
            }
            else if (keys.length === 1) {
                mem = mem + "{{" + keys[0] + "}}";
            }
            else {
                // not a valid interpolation object (can only contain one value plus format)
                warn("react-i18next: the passed in object contained more than one variable - the object should look like {{ value, format }} where format is optional.", child);
            }
        }
        else {
            warn("Trans: the passed in value is invalid - seems you passed in a variable like {number} - please pass in variables for interpolation as full objects like {{number}}.", child);
        }
    });
    return mem;
}
exports.nodesToString = nodesToString;
function renderNodes(children, targetString, i18n, i18nOptions, combinedTOpts) {
    if (targetString === '')
        return [];
    // check if contains tags we need to replace from html string to react nodes
    var keepArray = i18nOptions.transKeepBasicHtmlNodesFor || [];
    var emptyChildrenButNeedsHandling = targetString && new RegExp(keepArray.join('|')).test(targetString);
    // no need to replace tags in the targetstring
    if (!children && !emptyChildrenButNeedsHandling)
        return [targetString];
    // v2 -> interpolates upfront no need for "some <0>{{var}}</0>"" -> will be just "some {{var}}" in translation file
    var data = {};
    function getData(childs) {
        if (Object.prototype.toString.call(childs) !== '[object Array]')
            childs = [childs];
        childs.forEach(function (child) {
            if (typeof child === 'string')
                return;
            if (hasChildren(child))
                getData(getChildren(child));
            else if (typeof child === 'object' && !React.isValidElement(child))
                Object.assign(data, child);
        });
    }
    getData(children);
    targetString = i18n.services.interpolator.interpolate(targetString, __assign(__assign({}, data), combinedTOpts), i18n.language);
    // parse ast from string with additional wrapper tag
    // -> avoids issues in parser removing prepending text nodes
    var ast = HTML.parse("<0>" + targetString + "</0>");
    function mapAST(reactNodes, astNodes) {
        if (Object.prototype.toString.call(reactNodes) !== '[object Array]')
            reactNodes = [reactNodes];
        if (Object.prototype.toString.call(astNodes) !== '[object Array]')
            astNodes = [astNodes];
        return astNodes.reduce(function (mem, node, i) {
            var translationContent = node.children && node.children[0] && node.children[0].content;
            if (node.type === 'tag') {
                var child = reactNodes[parseInt(node.name, 10)] || {};
                var isElement = React.isValidElement(child);
                if (typeof child === 'string') {
                    mem.push(child);
                }
                else if (hasChildren(child)) {
                    var childs = getChildren(child);
                    var mappedChildren = mapAST(childs, node.children);
                    var inner = hasValidReactChildren(childs) && mappedChildren.length === 0
                        ? childs
                        : mappedChildren;
                    if (child.dummy)
                        child.children = inner; // needed on preact!
                    mem.push(React.cloneElement(child, __assign(__assign({}, child.props), { key: i }), inner));
                }
                else if (emptyChildrenButNeedsHandling &&
                    typeof child === 'object' &&
                    child.dummy &&
                    !isElement) {
                    // we have a empty Trans node (the dummy element) with a targetstring that contains html tags needing
                    // conversion to react nodes
                    // so we just need to map the inner stuff
                    var inner = mapAST(reactNodes /* wrong but we need something */, node.children);
                    mem.push(React.cloneElement(child, __assign(__assign({}, child.props), { key: i }), inner));
                }
                else if (isNaN(node.name) && i18nOptions.transSupportBasicHtmlNodes) {
                    if (node.voidElement) {
                        mem.push(React.createElement(node.name, { key: node.name + "-" + i }));
                    }
                    else {
                        var inner = mapAST(reactNodes /* wrong but we need something */, node.children);
                        mem.push(React.createElement(node.name, { key: node.name + "-" + i }, inner));
                    }
                }
                else if (typeof child === 'object' && !isElement) {
                    var content = node.children[0] ? translationContent : null;
                    // v1
                    // as interpolation was done already we just have a regular content node
                    // in the translation AST while having an object in reactNodes
                    // -> push the content no need to interpolate again
                    if (content)
                        mem.push(content);
                }
                else if (node.children.length === 1 && translationContent) {
                    // If component does not have children, but translation - has
                    // with this in component could be components={[<span class='make-beautiful'/>]} and in translation - 'some text <0>some highlighted message</0>'
                    mem.push(React.cloneElement(child, __assign(__assign({}, child.props), { key: i }), translationContent));
                }
                else {
                    mem.push(child);
                }
            }
            else if (node.type === 'text') {
                mem.push(node.content);
            }
            return mem;
        }, []);
    }
    // call mapAST with having react nodes nested into additional node like
    // we did for the string ast from translation
    // return the children of that extra node to get expected result
    var result = mapAST([{ dummy: true, children: children }], ast);
    return getChildren(result[0]);
}
function Interpolate(_a) {
    var children = _a.children, count = _a.count, parent = _a.parent, i18nKey = _a.i18nKey, tOptions = _a.tOptions, values = _a.values, defaults = _a.defaults, components = _a.components, ns = _a.ns, i18n = _a.i18n, additionalProps = __rest(_a, ["children", "count", "parent", "i18nKey", "tOptions", "values", "defaults", "components", "ns", "i18n"]);
    var i18nextInstance = i18n.i18nextInstance;
    if (!i18nextInstance) {
        warnOnce('You will need pass in an i18next instance by passing in i18n');
        return React.createElement(React.Fragment, null, children);
    }
    var t = i18nextInstance.t.bind(i18nextInstance);
    var reactI18nextOptions = __assign({}, (i18nextInstance.options && i18nextInstance.options.react));
    var useAsParent = parent !== undefined ? parent : 'div'; // reactI18nextOptions.defaultTransParent
    var defaultValue = defaults ||
        nodesToString('', children, 0, reactI18nextOptions) ||
        reactI18nextOptions.transEmptyNodeValue;
    var hashTransKey = reactI18nextOptions.hashTransKey;
    var key = i18nKey || (hashTransKey ? hashTransKey(defaultValue) : defaultValue);
    var interpolationOverride = values
        ? {}
        : { interpolation: { prefix: '#$?', suffix: '?$#' } };
    var combinedTOpts = __assign(__assign(__assign(__assign({}, tOptions), values), interpolationOverride), { defaultValue: defaultValue, count: count, ns: ns });
    var translation = key ? t(key, combinedTOpts) : defaultValue;
    if (!useAsParent)
        return renderNodes(components || children, translation, i18nextInstance, reactI18nextOptions, combinedTOpts);
    return React.createElement(useAsParent, additionalProps, renderNodes(components || children, translation, i18nextInstance, reactI18nextOptions, combinedTOpts));
}
exports.default = Interpolate;
